(function () {
  'use strict';

  // Track initialized instances by element so we don't double-bind listeners
  // when Shopify's editor re-renders the section.
  const initializedCarts = new WeakSet();

  function initStickyCart(root) {
    const stickyCart = (root || document).querySelector('#sticky-add-to-cart') ||
      (root && root.id === 'sticky-add-to-cart' ? root : null);

    if (!stickyCart || initializedCarts.has(stickyCart)) {
      return;
    }

    const settings = {
      desktopEnabled: stickyCart.dataset.desktopEnabled === 'true',
      mobileEnabled: stickyCart.dataset.mobileEnabled === 'true',
      triggerOffset: Number(stickyCart.dataset.triggerOffset) || 900
    };

    // Note: we no longer bail out early based on the current viewport here.
    // updateVisibility() (called at the end of init, and again on resize)
    // decides show/hide dynamically, so resizing across the 750px breakpoint
    // works correctly without needing to re-run the whole init sequence.

    const mainProductForm = document.querySelector('product-form form') ||
      document.querySelector('[id*="product-form"]') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!mainProductForm) {
      return;
    }

    const elements = {
      stickyVariantSelects: stickyCart.querySelectorAll('.sticky-variant-option'),
      stickyVariantId: stickyCart.querySelector('#sticky-variant-id'),
      stickyPrice: stickyCart.querySelector('#sticky-price'),
      stickyImage: stickyCart.querySelector('#sticky-product-image'),
      stickyButton: stickyCart.querySelector('#sticky-add-button'),
      stickyButtonText: stickyCart.querySelector('.sticky-button-text'),
      stickyForm: stickyCart.querySelector('#sticky-product-form'),
      mainAddButton: mainProductForm.querySelector('[name="add"], [type="submit"]'),
      spinner: stickyCart.querySelector('.loading-overlay__spinner'),
      mainVariantSelects: mainProductForm.querySelectorAll('select[name*="options"]')
    };

    let isVisible = false;
    let isProcessing = false;

    function formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
      const amount = cents / 100;
      let currencyCode = 'USD';
      try {
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
          currencyCode = window.Shopify.currency.active;
        }
      } catch (e) { /* noop */ }
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(amount);
      } catch (e) {
        return amount.toFixed(2);
      }
    }

    function debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
      };
    }

    // Elements that make up the theme's native "Add to cart" area, hidden
    // on viewports where the sticky bar is meant to fully replace it.
    function getMainButtonGroup() {
      const group = [];
      if (elements.mainAddButton) group.push(elements.mainAddButton);
      const dynamicCheckout = mainProductForm.querySelector('.shopify-payment-button');
      if (dynamicCheckout) group.push(dynamicCheckout);
      return group;
    }

    function setMainButtonHidden(hidden) {
      getMainButtonGroup().forEach((el) => {
        el.style.display = hidden ? 'none' : '';
      });
    }

    // Sticky bar always shows (no scroll trigger) whenever it's enabled for
    // the current viewport, and it replaces the theme's native buy button
    // on that same viewport, AND only while scroll position is within
    // settings.triggerOffset — past that point it hides, and it reappears
    // once the user scrolls back up above that same threshold.
    function updateVisibility() {
      const isMobileNow = window.innerWidth < 750;
      const enabledForViewport = isMobileNow ? settings.mobileEnabled : settings.desktopEnabled;
      const pastTriggerPoint = window.scrollY > settings.triggerOffset;
      const shouldShowNow = enabledForViewport && !pastTriggerPoint;

      if (shouldShowNow) {
        showStickyCart();
        setMainButtonHidden(true);
      } else {
        hideStickyCart();
        // Only restore the main form's native button if the sticky bar is
        // hidden because it's disabled for this viewport. If it's hidden
        // because we've scrolled past the trigger point, leave the native
        // button hidden too — the whole widget is meant to vanish, not
        // fall back to the inline buy box.
        setMainButtonHidden(enabledForViewport);
      }
    }

    function showStickyCart() {
      isVisible = true;
      stickyCart.classList.add('visible');
      document.body.style.paddingBottom = stickyCart.offsetHeight + 'px';
    }

    function hideStickyCart() {
      isVisible = false;
      stickyCart.classList.remove('visible');
      document.body.style.paddingBottom = '';
    }

    function syncWithMainForm() {
      try {
        const mainSelectedVariant = getMainSelectedVariant();
        if (mainSelectedVariant) {
          updateStickyVariant(mainSelectedVariant);
        }
      } catch (error) { /* noop */ }
    }

    function getMainSelectedVariant() {
      const selectedOptions = [];
      elements.mainVariantSelects.forEach((select, index) => {
        if (select.value) selectedOptions[index] = select.value;
      });

      if (selectedOptions.length > 0) {
        const variant = findVariantByOptions(selectedOptions);
        if (variant) {
          return {
            id: variant.id,
            price: variant.price,
            image: variant.featured_media ? variant.featured_media.preview_image.src : null,
            available: variant.available !== false,
            title: variant.title || selectedOptions.join(' - '),
            options: selectedOptions
          };
        }
      }

      const hiddenInput = mainProductForm.querySelector('input[name="id"]');
      if (hiddenInput && hiddenInput.value) {
        const variant = getVariantById(hiddenInput.value);
        if (variant) {
          return {
            id: variant.id,
            price: variant.price,
            image: variant.featured_media ? variant.featured_media.preview_image.src : null,
            available: variant.available !== false,
            title: variant.title,
            options: [variant.option1, variant.option2, variant.option3].filter(Boolean)
          };
        }
        return {
          id: hiddenInput.value,
          price: getCurrentPriceFromDOM(),
          image: getCurrentImageFromDOM(),
          available: true,
          title: getCurrentTitleFromDOM(),
          options: selectedOptions
        };
      }
      return null;
    }

    function getCurrentPriceFromDOM() {
      const priceElement = document.querySelector('.price .price-item--regular, .price .price-item');
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[^\d]/g, '');
        return priceText ? parseInt(priceText) : null;
      }
      return null;
    }

    function getCurrentImageFromDOM() {
      const imageElement = document.querySelector('.product__media img, .product-media img');
      return imageElement ? imageElement.src : null;
    }

    function getCurrentTitleFromDOM() {
      const titleElement = document.querySelector('.product__title h1, .product-title h1');
      return titleElement ? titleElement.textContent.trim() : '';
    }

    function findVariantByOptions(selectedOptions) {
      const variants = getVariantsFromJSON();
      if (!variants || !Array.isArray(variants)) return null;
      return variants.find((variant) => {
        if (!variant) return false;
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return selectedOptions.every((option, index) => option === variantOptions[index]);
      });
    }

    function getVariantById(variantId) {
      const variants = getVariantsFromJSON();
      if (!variants || !Array.isArray(variants)) return null;
      return variants.find((variant) => variant && variant.id && variant.id.toString() === variantId.toString());
    }

    function getVariantsFromJSON() {
      const variantScripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of variantScripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (Array.isArray(data) && data.length > 0 && data[0].id) return data;
        } catch (e) { /* continue */ }
      }

      const variantSelectsEl = document.querySelector('variant-selects');
      if (variantSelectsEl && variantSelectsEl.dataset && variantSelectsEl.dataset.variants) {
        try {
          const data = JSON.parse(variantSelectsEl.dataset.variants);
          if (Array.isArray(data)) return data;
        } catch (e) { /* continue */ }
      }

      const productScripts = document.querySelectorAll('script');
      for (const script of productScripts) {
        const content = script.textContent;
        if (content.includes('"variants"') && content.includes('"id"')) {
          try {
            const matches = content.match(/\[{"id":\d+.*?\]\s*(?:,|\})/);
            if (matches) {
              const variantData = JSON.parse(matches[0].replace(/,$/, ''));
              if (Array.isArray(variantData)) return variantData;
            }
          } catch (e) { /* continue */ }
        }
      }

      const mainSelects = mainProductForm.querySelectorAll('select[name*="options"]');
      const hiddenInput = mainProductForm.querySelector('input[name="id"]');
      if (mainSelects.length > 0 && hiddenInput && hiddenInput.value) {
        const selectedOptions = [];
        mainSelects.forEach((select) => selectedOptions.push(select.value));
        // No reliable JSON source found — infer availability from the main
        // form's own add-to-cart button rather than assuming true, since the
        // theme already sets disabled/aria-disabled correctly on that button.
        const mainAddBtn = mainProductForm.querySelector('[name="add"], [type="submit"]');
        const inferredAvailable = mainAddBtn
          ? !(mainAddBtn.disabled || mainAddBtn.getAttribute('aria-disabled') === 'true')
          : true;
        return [{
          id: parseInt(hiddenInput.value),
          available: inferredAvailable,
          option1: selectedOptions[0] || null,
          option2: selectedOptions[1] || null,
          option3: selectedOptions[2] || null,
          price: null
        }];
      }
      return null;
    }

    function updateStickyVariant(variant) {
      if (!variant) return;

      if (elements.stickyVariantId && variant.id) {
        elements.stickyVariantId.value = variant.id;
      }

      if (variant.options && elements.stickyVariantSelects.length > 0) {
        elements.stickyVariantSelects.forEach((select, index) => {
          if (variant.options[index] && select.value !== variant.options[index]) {
            const optionExists = Array.from(select.options).some((option) => option.value === variant.options[index]);
            if (optionExists) select.value = variant.options[index];
          }
        });
      }

      if (elements.stickyPrice && variant.price) {
        elements.stickyPrice.classList.add('updating');
        setTimeout(() => {
          const priceItem = elements.stickyPrice.querySelector('.price-item');
          if (priceItem) {
            priceItem.textContent = formatMoney(parseInt(variant.price));
            priceItem.dataset.price = variant.price;
          }
          elements.stickyPrice.classList.remove('updating');
        }, 150);
      }

      if (elements.stickyImage && variant.image) {
        elements.stickyImage.style.opacity = '0.6';
        let imageSrc = variant.image;
        if (!imageSrc.startsWith('http') && !imageSrc.startsWith('//')) {
          imageSrc = imageSrc.startsWith('/') ? imageSrc : '/' + imageSrc;
        }
        elements.stickyImage.src = imageSrc;
        elements.stickyImage.onload = () => { elements.stickyImage.style.opacity = '1'; };
        elements.stickyImage.onerror = () => { elements.stickyImage.style.opacity = '1'; };
      }

      updateButtonState(variant.available !== false);
    }

    function updateButtonState(available) {
      if (!elements.stickyButton || !elements.stickyButtonText) return;
      if (available) {
        elements.stickyButton.disabled = false;
        elements.stickyButtonText.textContent = 'Add to bag';
        elements.stickyButton.style.backgroundColor = '';
        elements.stickyButton.style.borderColor = '';
      } else {
        elements.stickyButton.disabled = true;
        elements.stickyButtonText.textContent = 'Sold out';
      }
    }

    function handleStickyFormSubmit(event) {
      event.preventDefault();
      if (isProcessing || elements.stickyButton.getAttribute('aria-disabled') === 'true') return;

      isProcessing = true;
      elements.stickyButton.setAttribute('aria-disabled', 'true');
      showLoading();

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      let config;
      if (typeof fetchConfig === 'function') {
        config = fetchConfig('javascript');
      } else {
        config = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      }
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(elements.stickyForm);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'sticky-cart',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message
              });
            }
            if (response.description && response.description.includes('sold out')) {
              elements.stickyButton.setAttribute('aria-disabled', 'true');
              elements.stickyButtonText.textContent = 'Sold out';
            }
            showError(response.description || 'Unable to add item to cart');
            return;
          }
          if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'sticky-cart',
              productVariantId: formData.get('id'),
              cartData: response
            });
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cartData: response } }));
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { variantId: formData.get('id'), cartData: response } }));
          showSuccess();
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            updateCartCount();
          }
        })
        .catch((error) => {
          showError(error.message || 'Network error occurred');
        })
        .finally(() => {
          isProcessing = false;
          hideLoading();
          const currentCart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (currentCart && currentCart.classList.contains('is-empty')) {
            currentCart.classList.remove('is-empty');
          }
          if (!elements.stickyButton.textContent.includes('Error') && !elements.stickyButton.textContent.includes('Sold out')) {
            elements.stickyButton.removeAttribute('aria-disabled');
          }
        });
    }

    function showLoading() {
      if (elements.stickyButton) {
        elements.stickyButton.classList.add('loading');
        elements.stickyButton.setAttribute('aria-disabled', 'true');
      }
      if (elements.spinner) elements.spinner.classList.remove('hidden');
      if (elements.stickyButtonText) elements.stickyButtonText.style.opacity = '0';
    }

    function hideLoading() {
      if (elements.stickyButton) elements.stickyButton.classList.remove('loading');
      if (elements.spinner) elements.spinner.classList.add('hidden');
      if (elements.stickyButtonText) elements.stickyButtonText.style.opacity = '1';
    }

    function showSuccess() {
      const originalText = elements.stickyButtonText.textContent;
      elements.stickyButtonText.textContent = 'Added!';
      elements.stickyButton.style.backgroundColor = '#28a745';
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        if (typeof cart.open === 'function') {
          setTimeout(() => cart.open(), 300);
        } else if (typeof cart.show === 'function') {
          setTimeout(() => cart.show(), 300);
        } else if (cart.classList.contains('cart-drawer')) {
          setTimeout(() => cart.classList.add('is-open'), 300);
        }
      }
      setTimeout(() => {
        elements.stickyButtonText.textContent = originalText;
        elements.stickyButton.style.backgroundColor = '';
      }, 2000);
    }

    function showError() {
      const originalText = elements.stickyButtonText.textContent;
      elements.stickyButtonText.textContent = 'Error';
      elements.stickyButton.style.backgroundColor = '#dc3545';
      setTimeout(() => {
        elements.stickyButtonText.textContent = originalText;
        elements.stickyButton.style.backgroundColor = '';
      }, 3000);
    }

    function updateCartCount() {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cartData) => {
          const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-count');
          cartCountElements.forEach((element) => { element.textContent = cartData.item_count; });
        })
        .catch(() => {});
    }

    if (elements.stickyForm) {
      elements.stickyForm.addEventListener('submit', handleStickyFormSubmit);
    }

    elements.stickyVariantSelects.forEach((select) => {
      select.addEventListener('change', function () {
        try {
          const mainSelect = mainProductForm.querySelector(`select[name="${this.name}"]`);
          if (mainSelect) {
            mainSelect.value = this.value;
            mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
          const selectedOptions = [];
          elements.stickyVariantSelects.forEach((sel) => {
            selectedOptions[sel.dataset.optionIndex] = sel.value;
          });
          const variant = findVariantByOptions(selectedOptions);
          if (variant) {
            if (elements.stickyVariantId) elements.stickyVariantId.value = variant.id;
            const mainHiddenInput = mainProductForm.querySelector('input[name="id"]');
            if (mainHiddenInput) mainHiddenInput.value = variant.id;
            updateStickyVariant({
              id: variant.id,
              price: variant.price,
              image: variant.featured_media ? variant.featured_media.preview_image.src : null,
              available: variant.available,
              title: variant.title,
              options: selectedOptions
            });
          }
        } catch (error) { /* noop */ }
      });
    });

    elements.mainVariantSelects.forEach((select) => {
      select.addEventListener('change', debounce(() => {
        try {
          const stickySelect = stickyCart.querySelector(`select[name="${select.name}"]`);
          if (stickySelect && stickySelect.value !== select.value) {
            stickySelect.value = select.value;
          }
          syncWithMainForm();
        } catch (error) { /* noop */ }
      }, 100));
    });

    const mainHiddenInput = mainProductForm.querySelector('input[name="id"]');
    let pollInterval;
    if (mainHiddenInput) {
      try {
        const observer = new MutationObserver(() => syncWithMainForm());
        observer.observe(mainHiddenInput, { attributes: true, attributeFilter: ['value'] });

        let lastValue = mainHiddenInput.value;
        pollInterval = setInterval(() => {
          if (mainHiddenInput.value !== lastValue) {
            lastValue = mainHiddenInput.value;
            syncWithMainForm();
          }
        }, 500);
      } catch (error) { /* noop */ }
    }

    window.addEventListener('resize', debounce(() => {
      updateVisibility();
    }, 250));

    let scrollTicking = false;
    window.addEventListener('scroll', () => {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(() => {
        updateVisibility();
        scrollTicking = false;
      });
    }, { passive: true });

    // Clean up interval/observer if the editor removes this section, and
    // restore the native button in case this instance is torn down.
    document.addEventListener('shopify:section:unload', function onUnload(event) {
      if (event.target.contains(stickyCart)) {
        if (pollInterval) clearInterval(pollInterval);
        setMainButtonHidden(false);
        document.removeEventListener('shopify:section:unload', onUnload);
      }
    });

    try {
      if (window.location.pathname.includes('/cart')) {
        stickyCart.style.display = 'none';
        initializedCarts.add(stickyCart);
        return;
      }

      const initialVariant = getMainSelectedVariant();
      if (initialVariant) {
        updateStickyVariant(initialVariant);
      } else if (elements.stickyVariantId) {
        const hiddenIdInput = mainProductForm.querySelector('input[name="id"]');
        if (hiddenIdInput && hiddenIdInput.value) {
          elements.stickyVariantId.value = hiddenIdInput.value;
        }
      }

      updateVisibility();
      stickyCart.dataset.initialized = 'true';
    } catch (error) {
      stickyCart.dataset.initialized = 'true';
    }

    initializedCarts.add(stickyCart);
  }

  function bootAll(root) {
    document.querySelectorAll.call(root || document, '#sticky-add-to-cart').forEach((el) => initStickyCart(el));
    // handle case where root itself is the sticky cart element
    if (root && root.id === 'sticky-add-to-cart') initStickyCart(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootAll());
  } else {
    bootAll();
  }

  // Re-run whenever the theme editor swaps this section's HTML back in.
  // This fires on every save/setting change in Customize mode, and is the
  // reason the sticky bar can appear "dead" in the editor: inline <script>
  // tags injected via innerHTML never execute, so without this listener
  // initStickyCart() would simply never run again after the first load.
  document.addEventListener('shopify:section:load', function (event) {
    bootAll(event.target);
  });
})();